/**
 * src/views/FocusView.jsx — CEO Focus Dashboard + Task Manager (Orchestrator)
 * AC Nautitech Executive Assistant
 *
 * Dashboard KPIs + AI Focus (dash sub-view) and filtered task list (tasks sub-view).
 * Tasks persist across refreshes via Airtable proxy.
 *
 * ── Sub-Components ──────────────────────────────
 *   DashboardSection → src/views/focus/DashboardSection.jsx
 *   TaskEdit         → src/views/focus/TaskEdit.jsx
 *
 * ── Shared Components ──────────────────────────
 *   FilterBar        → src/components/shared/FilterBar.jsx
 *   TaskDetailPanel  → src/components/shared/TaskDetailPanel.jsx
 *   DelayReasonModal → src/lib/AppDataContext.jsx (transparent intercept)
 *
 * ── Data Flow ──────────────────────────────────
 *   AppDataContext → filters persisted per view via setViewFilter()
 *   All mutations go through context (optimistic + rollback)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { nextTaskStatus } from "../lib/taskStatus";
import { priorityToP } from "../lib/constants/priority";
import { AnimatePresence } from "framer-motion";
import { Plus, AlertCircle, Anchor, ChevronDown,
  CheckSquare, Sparkles, Mic, Activity, Bell, Package, Clock, Columns } from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor,
  useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import SortableTaskRow from "./focus/SortableTaskRow";
import { supabase } from "../lib/supabase.js";

import BreadcrumbCard from "../components/shared/BreadcrumbCard";
import useVoiceBreadcrumbs from "../hooks/useVoiceBreadcrumbs";
import { useAppDataContext } from "../lib/AppDataContext";
import { useT } from '../lib/i18n/I18nContext';
import { applyContract } from "../lib/contracts/renderContract";
import TaskDetailPanel from "../components/shared/TaskDetailPanel";
import useBreakpoint from "../hooks/useBreakpoint";
import TaskCard from "../components/shared/TaskCard";
import AssignmentBanner from "../components/shared/AssignmentBanner";
import RemindersDue from "./focus/RemindersDue"; // TICKET-735
import FilterBar from "../components/shared/FilterBar";
import { useSchemaOptions } from "../lib/gantt/useSchemaOptions";
import { ModalSheet, SkeletonDashboard } from "../components/SharedUI";
import Button from "../components/shared/Button";
import DashboardSection from "./focus/DashboardSection";
import TaskEdit from "./focus/TaskEdit";
import AssignedToMe from "./focus/AssignedToMe"; // TICKET-209 P3
import FocusBoard from "./focus/FocusBoard";
import EntityMicButton from "../components/shared/EntityMicButton";
import ActiveVoiceStrip from "../components/voice/ActiveVoiceStrip";
import AddFab from "../components/shared/AddFab";
import { fetchActivityFeed, fetchPendingDecisions } from "../lib/api/cfo";
import { updateMaterialRequest } from "../lib/api/procurement";

/* ═══ DATA ═══ */
const PRIORITIES = {
  P1: { c: "var(--p1)", b: "var(--p1b)", r: "var(--bad)" },
  P2: { c: "var(--p2)", b: "var(--p2b)", r: "var(--warn)" },
  P3: { c: "var(--p3)", b: "var(--p3b)", r: "var(--ok)" },
};

const generateId = () => Math.random().toString(36).slice(2, 9);
const today = () => new Date().toISOString().slice(0, 10);

/* ═══ TASK ROW MAPPING (raw snake_case → view shape) ═══
 * FIX[T2,T7,T15]: read raw columns directly per SHAPE_CONTRACT.
 * Status is canonical lowercase slug; helpers in src/lib/constants/taskStatus.
 */
import { normalizeTaskStatus, taskStatusLabel, isDone, isBlocked } from '../lib/constants/taskStatus';
import ViewContainer from '../components/shared/ViewContainer';
import { isQuoteReviewTask } from '../lib/nav/quoteNav';

function mapTaskRow(record, peopleLookup) {
  const assigneeId = record.assignee_id || '';
  const personName = assigneeId
    ? (record.user_profiles?.display_name || peopleLookup[assigneeId] || '')
    : '';
  return {
    id: record.id,
    title: record.task_name || '',
    // Raw DB value is 'high' | 'medium' | 'low' (migration 065 CHECK).
    // Normalize to canonical P1/P2/P3 via priorityToP so filter + display match.
    pr: priorityToP(record.priority),
    st: normalizeTaskStatus(record.status),
    area: record.department || 'General',
    due: record.due_date || '',
    notes: record.notes || record.short_description || '',
    who: personName,
    whoId: assigneeId,
    projectId: record.project_id || '',
    bundleId: record.bundle_id || '',
    // TICKET-1098: TaskCard + the Review lane read the raw tag (quote_review:<id>).
    work_tag: record.work_tag || null,
    budgetHours: record.estimated_hours ?? '',
    taskOrder: record.task_order || 0,
    cr: record.created_at ? new Date(record.created_at).getTime() : Date.now(),
    _persisted: true,
  };
}

/* Task list order. Exported for tests (TICKET-1098: quote reviews pinned above P1). */
export function compareFocusTasks(a, b, sortBy) {
  // TICKET-1098: quote reviews are a lane ABOVE P1 — pinned first under every sort.
  const ra = isQuoteReviewTask(a), rb = isQuoteReviewTask(b);
  if (ra !== rb) return ra ? -1 : 1;
  if (sortBy === 'due') {
    const aDue = a.due || '9999', bDue = b.due || '9999';
    return aDue.localeCompare(bDue);
  }
  if (sortBy === 'created') {
    return (b.cr || 0) - (a.cr || 0); // newest first
  }
  // default: priority then status (canonical slugs)
  const priorityOrder = { P1: 0, P2: 1, P3: 2 };
  const statusOrder = { blocked: 0, todo: 1, in_progress: 2, review: 3, done: 4, cancelled: 5 };
  return priorityOrder[a.pr] !== priorityOrder[b.pr]
    ? (priorityOrder[a.pr] ?? 2) - (priorityOrder[b.pr] ?? 2) // ?? not ||: P1 is 0 (sorted P1 after P2)
    : (statusOrder[a.st] || 0) - (statusOrder[b.st] || 0);
}

/* ═══ SORTABLE TASK ROW ═══ */

// SortableTaskRow now lives in ./focus/SortableTaskRow.jsx and is reused by
// both this view's "My Tasks" list and FocusBoard's kanban columns so the
// drag behavior, grip visibility, and click semantics stay in lockstep.

/* ═══════════════════════════════════════════════════════════
   FOCUS VIEW — exported, no shell
   ═══════════════════════════════════════════════════════════ */
export default function FocusView() {
  const {
    tasks: rawTasks,
    projects: rawProjects,
    people,
    bundles,
    peopleLookup,
    projectLookup,
    loading,
    error: ctxError,
    silentReload,
    handleCreateTask,
    handleUpdateTask,
    handleUpdateStatus,
    handleDeleteTask,
    handleCreateTimeEntry,
    timeLogs,
    getViewFilters,
    setViewFilter,
    clearViewFilters,
  } = useAppDataContext();

  const schemaOptions = useSchemaOptions();
  const { t } = useT();
  const { isMobile } = useBreakpoint();

  // ── Filter persistence (focus view) ──────────────────
  const persistedFilters = getViewFilters('focus');
  const [view, setView] = useState("dash"); // "dash" | "tasks" | "board"
  const [modal, setModal] = useState(null);
  const [area, setArea] = useState(persistedFilters.department || "All");
  const [pri, setPri] = useState("All");
  const [search, setSearch] = useState(persistedFilters.search || "");
  const [statusFilter, setStatusFilter] = useState(persistedFilters.status || "");
  const [assigneeFilter, setAssigneeFilter] = useState(persistedFilters.assignee || "");
  const [projectFilter, setProjectFilter] = useState(persistedFilters.project || "");
  const [sortBy, setSortBy] = useState("priority"); // priority | due | created
  const [showDone, setShowDone] = useState(true);

  const [localError, setLocalError] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  // Local-only captured tasks (not yet persisted or pending persist)
  const [pendingTasks, setPendingTasks] = useState([]);

  // Role and identity for TaskDetailPanel evidence sections
  const userRole = localStorage.getItem('effectiveRole') || 'guest';
  const [currentUserId, setCurrentUserId] = useState(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUserId(session?.user?.id || null);
    });
  }, []);

  // SmartSearchOverlay navigation: when a task is clicked in the search
  // results AppShell switches to the Focus tab and fires acn:search-open.
  // We listen for it and open the TaskDetailPanel for the matching row.
  // Uses a ref to rawTasks so the listener always sees fresh data without
  // re-subscribing on every render.
  const rawTasksRef = useRef(null);
  useEffect(() => { rawTasksRef.current = rawTasks; }, [rawTasks]);
  useEffect(() => {
    const handler = (e) => {
      const { type, id } = e.detail || {};
      if (type !== 'task' || !id) return;
      const tasks = rawTasksRef.current || [];
      const raw = tasks.find(r => r.id === id);
      if (raw) setTaskDetail(raw);
    };
    window.addEventListener('acn:search-open', handler);
    return () => window.removeEventListener('acn:search-open', handler);
  }, []);

  // C1: Activity feed + C4: Decisions inbox
  const [activities, setActivities] = useState([]);
  const [decisions, setDecisions] = useState({ pending_pos: [], pending_requests: [], total: 0 });
  useEffect(() => {
    fetchActivityFeed().then(setActivities).catch(() => {});
    fetchPendingDecisions().then(d => d && setDecisions(d)).catch(() => {});
  }, []);

  // Dispatch context for floating agent bubble
  useEffect(() => {
    const proj = projectFilter ? rawProjects.find(p => p.id === projectFilter) : null;
    window.dispatchEvent(new CustomEvent('view-context-change', {
      detail: {
        view: 'focus',
        projectId: proj?.id || null,
        projectName: proj?.project_name || null,
      },
    }));
  }, [projectFilter, rawProjects]);

  // Voice Notes section — auto-refreshes after recording
  const { notes: voiceNotes, refresh: loadVoiceNotes } = useVoiceBreadcrumbs({ contextType: projectFilter ? 'project' : 'focus', contextId: projectFilter || undefined, limit: 5 });
  const [voiceExpanded, setVoiceExpanded] = useState(false);

  // Auto-dismiss local errors
  useEffect(() => {
    if (localError) {
      const timer = setTimeout(() => setLocalError(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [localError]);

  // Phase B2 — gate raw tasks through focus render contract before mapping.
  // applyContract emits render_exclusion for archived / missing task_name rows.
  const { visible: contractedRawTasks } = useMemo(
    () => applyContract('focus', 'task', rawTasks || []),
    [rawTasks],
  );

  // Derive mapped tasks from context + merge pending local tasks
  const mappedTasks = useMemo(() => {
    const ctxMapped = contractedRawTasks.map(r => mapTaskRow(r, peopleLookup));
    return [...pendingTasks, ...ctxMapped];
  }, [contractedRawTasks, peopleLookup, pendingTasks]);

  // FIX[shape]: rawProjects are flat snake_case rows; no `_is_empty` legacy flag
  const projects = useMemo(() => rawProjects, [rawProjects]);


  const tasks = mappedTasks;
  // FIX[T4]: status is canonical slug now ('done', 'todo', etc.)
  const open = tasks.filter(t => !isDone(t.st));

  // Normalize incoming filter values to slugs (filter UI may still pass 'To Do')
  const statusFilterSlug = statusFilter ? normalizeTaskStatus(statusFilter) : '';

  const reviewTaskCount = open.filter(isQuoteReviewTask).length;

  const filtered = tasks.filter(t => {
    if (!showDone && isDone(t.st)) return false;
    if (area !== "All" && t.area !== area) return false;
    if (pri === "REV") { if (!isQuoteReviewTask(t)) return false; }
    else if (pri !== "All" && t.pr !== pri) return false;
    // FIX[T7]: coalesce title + notes for search
    // TICKET-531: assignee name too — the row shows it, so the search must find it.
    if (search) {
      const q = search.toLowerCase();
      const haystack = `${t.title || ''} ${t.notes || ''} ${t.who || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (statusFilterSlug && t.st !== statusFilterSlug) return false;
    if (assigneeFilter && t.whoId !== assigneeFilter) return false;
    if (projectFilter && t.projectId !== projectFilter) return false;
    return true;
  }).sort((a, b) => compareFocusTasks(a, b, sortBy));

  // ── Drag-and-drop reorder (tasks list view) ─────────
  const [localOrder, setLocalOrder] = useState(null); // null = use filtered order
  const [activeId, setActiveId] = useState(null);
  const prevStatusRef = useRef(new Map()); // task.id -> previous status before marking Done

  // Reset local order when filters/sort change
  const filterKey = `${area}|${pri}|${search}|${statusFilter}|${assigneeFilter}|${projectFilter}|${sortBy}|${showDone}`;
  useEffect(() => { setLocalOrder(null); }, [filterKey]);

  const orderedFiltered = useMemo(() => {
    if (!localOrder) return filtered;
    // Reorder filtered list based on localOrder id sequence
    const idxMap = new Map(localOrder.map((id, i) => [id, i]));
    const ordered = [...filtered].sort((a, b) => {
      const ai = idxMap.has(a.id) ? idxMap.get(a.id) : Infinity;
      const bi = idxMap.has(b.id) ? idxMap.get(b.id) : Infinity;
      return ai - bi;
    });
    return ordered;
  }, [filtered, localOrder]);

  const taskIds = useMemo(() => orderedFiltered.map(t => t.id), [orderedFiltered]);

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 10 } });
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(pointerSensor, touchSensor, keyboardSensor);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = taskIds.indexOf(active.id);
    const newIndex = taskIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newOrder = arrayMove(taskIds, oldIndex, newIndex);
    setLocalOrder(newOrder);

    // Persist new order to DB
    const minIdx = Math.min(oldIndex, newIndex);
    const maxIdx = Math.max(oldIndex, newIndex);
    for (let i = minIdx; i <= maxIdx; i++) {
      handleUpdateTask(newOrder[i], { task_order: i + 1 }).catch(() => {});
    }
  }, [taskIds, handleUpdateTask]);

  const handleDragCancel = useCallback(() => { setActiveId(null); }, []);
  const activeDragTask = activeId ? orderedFiltered.find(t => t.id === activeId) : null;

  const save = async (t) => {
    setModal(null);

    const isNew = !t._persisted;
    if (isNew) {
      // Optimistic: add to pending
      const pending = { ...t, _pending: true };
      setPendingTasks(prev => [pending, ...prev]);

      try {
        const result = await handleCreateTask({
          task_name: t.title,
          status: t.st,
          department: t.area,
          assignee_id: t.whoId || undefined,
          due: t.due || undefined,
          project_id: t.projectId || undefined,
          bundle_id: t.bundleId || undefined,
          estimated_hours: t.budgetHours ? parseFloat(t.budgetHours) : null,
        });
        if (result?.success) {
          setPendingTasks(prev => prev.filter(x => x.id !== t.id));
        } else {
          setPendingTasks(prev => prev.filter(x => x.id !== t.id));
          setLocalError(t('focus.taskCreationFailed'));
        }
      } catch {
        setPendingTasks(prev => prev.filter(x => x.id !== t.id));
        setLocalError('Task creation failed');
      }
    } else {
      // Existing Airtable task — update via context
      try {
        const result = await handleUpdateTask(t.id, {
          task_name: t.title,
          status: t.st,
          department: t.area,
          assignee_id: t.whoId || undefined,
          due: t.due || undefined,
          project_id: t.projectId || undefined,
          bundle_id: t.bundleId || undefined,
          notes: t.notes || undefined,
          estimated_hours: t.budgetHours ? parseFloat(t.budgetHours) : null,
        });
        if (!result?.success) {
          setLocalError(t('focus.taskUpdateFailed'));
        }
      } catch {
        setLocalError('Task update failed');
      }
    }
  };

  const del = async (id) => {
    setModal(null);
    // Check if it's a pending (local-only) task
    const isPending = pendingTasks.some(t => t.id === id);
    if (isPending) {
      setPendingTasks(prev => prev.filter(t => t.id !== id));
      return;
    }
    // Airtable task — context handles optimistic delete + rollback
    try {
      await handleDeleteTask(id);
    } catch {
      setLocalError(t('focus.failedToDelete'));
    }
  };

  const cycle = async (id) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    const nextStatus = nextTaskStatus(task.st, prevStatusRef.current, id);

    // Check if pending local task
    const isPending = pendingTasks.some(t => t.id === id);
    if (isPending) {
      setPendingTasks(prev => prev.map(t => t.id !== id ? t : { ...t, st: nextStatus }));
      return;
    }

    // Synthetic/test data — update client-side only (no DB record)
    // NOTE: setTasks doesn't exist in this scope (rawTasks comes from context).
    // The previous implementation was dead code; synthetic toggling now goes through
    // pendingTasks if pending, otherwise falls through to handleUpdateStatus below.
    const isSynthetic = id.startsWith('aaaaaaaa-') || id.startsWith('eeeeeeee-') || id.startsWith('ffffffff-');
    if (isSynthetic) {
      // No-op: synthetic rows are read from context; toggling lives in pending state.
      return;
    }

    // Real task — context handles optimistic update + rollback
    try {
      await handleUpdateStatus(id, nextStatus);
    } catch {
      // Context rolls back automatically
    }
  };

  const doneCount = tasks.filter(t => isDone(t.st)).length;
  const displayError = localError || ctxError;

  /* ── FilterBar bridge ─────────────────────────────── */
  const focusFilters = useMemo(() => ({
    search,
    department: area === 'All' ? '' : area,
    status: statusFilter,
    assignee: assigneeFilter,
    project: projectFilter,
  }), [search, area, statusFilter, assigneeFilter, projectFilter]);

  const handleFocusFilterChange = useCallback((key, value) => {
    if (key === 'search') { setSearch(value); setViewFilter('focus', 'search', value); }
    else if (key === 'department') { setArea(value || 'All'); setViewFilter('focus', 'department', value); }
    else if (key === 'status') { setStatusFilter(value); setViewFilter('focus', 'status', value); }
    else if (key === 'assignee') { setAssigneeFilter(value); setViewFilter('focus', 'assignee', value); }
    else if (key === 'project') { setProjectFilter(value); setViewFilter('focus', 'project', value); }
  }, [setViewFilter]);

  const handleFocusClearAll = useCallback(() => {
    setSearch(''); setArea('All'); setStatusFilter(''); setAssigneeFilter(''); setProjectFilter('');
    clearViewFilters('focus');
  }, [clearViewFilters]);

  // Loading state
  if (loading) {
    return <SkeletonDashboard />;
  }

  return (
    <ViewContainer testId="focus-view" maxWidth={null} style={{ padding: 0 }}>
      <div style={{padding:"0 0 80px",position:"relative"}}>
      {/* Error banner */}
      {displayError && <div style={{margin:"12px 16px 0",padding:"10px 16px",borderRadius:10,background:"var(--p1b)",color:"var(--bad)",fontSize:13,fontWeight:500,display:"flex",alignItems:"center",gap:8,animation:"fadeIn 0.2s"}}><AlertCircle size={16}/>{displayError}</div>}

      {/* TICKET-030: Assignment banner — surfaces unread task assignments. Renders nothing when unreadCount === 0. */}
      <AssignmentBanner onOpenTask={(taskId) => { const raw = rawTasks.find(r => r.id === taskId); if (raw) setTaskDetail(raw); }} />

      {/* TICKET-735 — reminder notes due/overdue (task log reminders). Renders nothing when empty. */}
      <RemindersDue onOpenTask={(taskId) => { const raw = rawTasks.find(r => r.id === taskId); if (raw) setTaskDetail(raw); }} />

      {/* Sub-nav: Dashboard / Tasks / Board toggle.
          TICKET-UI-BUTTON-SYSTEM: was a raw inverting navy fill + always-white text which is
          white-on-white in dark (--navy inverts light). Now the centralized
          `toggle` variant (active=var(--navy-surface)+var(--on-navy)) inside a
          glass segmented container — same treatment as the Pipeline toggle, so
          changing --navy-surface restyles every toggle app-wide. */}
      {/* Phone: text-only segments (icons dropped, labels + aria-label kept) and minWidth:0
          so the row fits — the ES labels + icons were 323px and pushed the mic 4px past a
          375px screen (mobile overflow audit). */}
      <div style={{display:"flex",gap:4,padding:"16px 16px 0",alignItems:"center"}}>
        <div style={{display:"flex",gap:2,background:"var(--glass)",borderRadius:99,padding:2,border:"1px solid var(--bdr)",minWidth:0}}>
          {[{k:"dash",l:t('focus.dashboard'),i:Sparkles},{k:"tasks",l:t('focus.myTasks'),i:CheckSquare},{k:"board",l:t('focus.board'),i:Columns}].map(({k,l,i:I}) => (
            <Button
              key={k}
              variant="toggle"
              active={view === k}
              onClick={() => setView(k)}
              aria-label={l}
              data-testid={`focus-view-${k}`}
              leftIcon={isMobile ? undefined : <I size={15} strokeWidth={1.5}/>}
              style={{padding:isMobile ? "8px 12px" : "8px 16px",borderRadius:99,fontSize:13,gap:6}}
            >{l}</Button>
          ))}
        </div>
        <div style={{flex:1}}/>
        {/* Build identity — moved into sub-nav row (marginLeft:auto via flex:1 spacer above)
            to avoid colliding with the fixed search button at top:8 right:8. Hidden on
            mobile: it was competing with EntityMicButton for the same row width, pushing
            the mic button past the right edge on iPhone (CEO 2026-08-06, screenshot). It's
            a debug/copy-build-info affordance, not a feature — desktop/tablet has room. */}
        {!isMobile && (
          <button
            type="button"
            data-testid="build-identity"
            onClick={() => {
              const txt = `v${__APP_VERSION__} • ${__APP_SHA__} • ${__APP_BUILD_TIME__}`;
              navigator.clipboard?.writeText(txt).catch(() => {});
            }}
            title={`Build: ${__APP_BUILD_TIME__} Barcelona — click to copy`}
            style={{
              fontSize:9,color:"var(--tm)",opacity:0.6,fontFamily:"monospace",
              background:"transparent",border:"none",cursor:"pointer",padding:"2px 4px",
            }}
          >
            v{__APP_VERSION__} · {__APP_SHA__}
          </button>
        )}
        <EntityMicButton contextType="general" />
      </div>

      {/* In-panel live voice progress for the top-right general mic (CEO 2026-06-10) */}
      <div style={{padding:"8px 16px 0"}}>
        <ActiveVoiceStrip contextType="general" />
      </div>

      {/* ═══ BREADCRUMBS SECTION ═══ */}
      {view !== 'board' && voiceNotes.length > 0 && (
        <div style={{padding:"12px 16px 0"}}>
          <button
            onClick={() => setVoiceExpanded(v => !v)}
            style={{
              display:"flex",alignItems:"center",gap:8,width:"100%",
              padding:"10px 14px",borderRadius:12,
              background:"var(--glass)",border:"1px solid var(--bdr)",
              cursor:"pointer",transition:"all 150ms",
            }}
          >
            <Mic size={16} color="var(--accent)" />
            <span style={{fontSize:14,fontWeight:600,color:"var(--t1)",flex:1,textAlign:"left"}}>{t('focus.breadcrumbs')}</span>
            <span style={{fontSize:11,fontWeight:600,color:"var(--tm)",background:"var(--stodob)",padding:"2px 8px",borderRadius:99}}>{voiceNotes.length}</span>
            <ChevronDown size={14} style={{color:"var(--tm)",transform:voiceExpanded?"rotate(180deg)":"rotate(0)",transition:"transform 200ms"}} />
          </button>

          {voiceExpanded && (
            <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:12,animation:"fadeIn 0.2s"}}>
              {voiceNotes.map(vn => (
                <BreadcrumbCard key={vn.id} breadcrumb={vn} onRefresh={loadVoiceNotes} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ DASHBOARD VIEW ═══ */}
      {view === "dash" && (<>
        <DashboardSection
          tasks={tasks}
          open={open}
          projects={projects}
          people={people}
          rawTasks={rawTasks}
          onSelectTask={setTaskDetail}
          onOpenModal={setModal}
        />

        {/* TICKET-209 P3 — Assigned to You feed (focus_pins delegated by CEO/admin) */}
        <AssignedToMe />

        {/* C4: Decisions Inbox */}
        {decisions.total > 0 && (
          <div style={{ margin: '16px 16px 0', padding: 16, borderRadius: 14, background: 'var(--glass)', border: '1px solid var(--bdr)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Bell size={16} color="var(--accent)" />
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)' }}>{t('focus.needsDecision')}</span>
              <span style={{ background: 'var(--bad-tint)', color: 'var(--bad)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>{decisions.total}</span>
            </div>
            {decisions.pending_pos.map(po => (
              <div key={po.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
                <Package size={13} color="var(--accent)" />
                <span style={{ flex: 1, color: 'var(--t1)', fontWeight: 500 }}>
                  PO {po.po_number} — {po.project_name} {po.supplier?.name ? `(${po.supplier.name})` : ''}
                </span>
                <span style={{ fontSize: 10, color: 'var(--warn)', fontWeight: 600, background: 'var(--warn-tint)', padding: '2px 8px', borderRadius: 4 }}>{t('focus.pendingApproval')}</span>
              </div>
            ))}
            {decisions.pending_requests.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
                <Package size={13} color="var(--warn)" />
                <span style={{ flex: 1, color: 'var(--t1)', fontWeight: 500 }}>
                  Request: {r.material_name} ({r.qty} {r.unit}) — {r.project_name || t('common.noProject')}
                  {r.urgency === 'critical' && <span style={{ color: 'var(--bad)', fontWeight: 700, marginLeft: 6 }}>{t('focus.critical')}</span>}
                  {r.urgency === 'urgent' && <span style={{ color: 'var(--warn)', fontWeight: 700, marginLeft: 6 }}>{t('focus.urgent')}</span>}
                </span>
                <button onClick={async () => {
                  await updateMaterialRequest(r.id, { status: 'approved' });
                  fetchPendingDecisions().then(d => d && setDecisions(d)).catch(() => {});
                }} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'var(--ok-tint)', color: 'var(--ok)', border: 'none', cursor: 'pointer' }}>
                  {t('focus.approve')}
                </button>
                <button onClick={async () => {
                  await updateMaterialRequest(r.id, { status: 'rejected' });
                  fetchPendingDecisions().then(d => d && setDecisions(d)).catch(() => {});
                }} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'var(--glass)', color: 'var(--bad)', border: '1px solid var(--bdr)', cursor: 'pointer' }}>
                  {t('focus.reject')}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* C1: Activity Feed */}
        {activities.length > 0 && (
          <div style={{ margin: '16px 16px 0', padding: 16, borderRadius: 14, background: 'var(--glass)', border: '1px solid var(--bdr)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Activity size={16} color="var(--accent)" />
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--t1)' }}>{t('focus.recentActivity')}</span>
              <span style={{ fontSize: 11, color: 'var(--tm)' }}>{t('focus.last24h')}</span>
            </div>
            {activities.slice(0, 15).map(a => {
              const icon = a.entity_type === 'task' ? CheckSquare
                : a.entity_type === 'material_request' ? Package
                : a.entity_type === 'po' ? Package
                : Activity;
              const Icon = icon;
              const actionColor = a.action === 'completed' ? 'var(--ok)'
                : a.action === 'status_changed' ? 'var(--accent)'
                : a.action === 'requested' ? 'var(--warn)'
                : 'var(--tm)';
              const timeAgo = (() => {
                const mins = Math.floor((Date.now() - new Date(a.created_at).getTime()) / 60000);
                if (mins < 60) return t('time.mAgo', { n: mins });
                const hrs = Math.floor(mins / 60);
                if (hrs < 24) return t('time.hAgo', { n: hrs });
                return t('time.dAgo', { n: Math.floor(hrs / 24) });
              })();
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
                  <Icon size={12} color={actionColor} />
                  <span style={{ flex: 1, color: 'var(--t1)' }}>
                    <span style={{ fontWeight: 600 }}>{a.entity_name || a.entity_id}</span>
                    <span style={{ color: 'var(--tm)', marginLeft: 6 }}>{a.action.replace('_', ' ')}</span>
                    {a.new_value && <span style={{ color: actionColor, marginLeft: 4, fontWeight: 600 }}>{a.new_value}</span>}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--tm)', flexShrink: 0 }}>{timeAgo}</span>
                </div>
              );
            })}
          </div>
        )}
      </>)}

      {/* ═══ SHARED FILTERS (Tasks + Board) ═══ */}
      {(view === "tasks" || view === "board") && <div style={{padding:"16px 16px 0"}}>
        {/* Task stats */}
        <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontSize:36,fontWeight:800,color:"var(--accent)",letterSpacing:"-0.03em",lineHeight:1,animation:"countUp 0.6s var(--ease)"}}>{open.length}<span style={{fontSize:14,fontWeight:400,color:"var(--t2)",marginLeft:6}}>{t('common.open')}</span></div>
          <div style={{display:"flex",gap:6}}>
            {(() => { const overdue = open.filter(t => t.due && t.due < today()).length; return overdue > 0 ? <span style={{background:"var(--p1b)",borderRadius:99,padding:"3px 10px",fontSize:11,fontWeight:600,color:"var(--bad)",display:"flex",alignItems:"center",gap:3}}><AlertCircle size={11}/>{overdue} {t('focus.overdue')}</span> : null; })()}
            {(() => { const blocked = open.filter(t => isBlocked(t.st)).length; return blocked > 0 ? <span style={{background:"var(--p2b)",borderRadius:99,padding:"3px 10px",fontSize:11,fontWeight:600,color:"var(--warn)"}}>{blocked} {t('focus.blocked')}</span> : null; })()}
          </div>
        </div>

        {/* ── Filter Bar ─────────────────────────────── */}
        <div style={{marginBottom:12}}>
          <FilterBar
            filters={focusFilters}
            onFilterChange={handleFocusFilterChange}
            onClearAll={handleFocusClearAll}
            projects={projects}
            people={people}
            bundles={bundles}
            schemaOptions={schemaOptions || {}}
            showSearch
            showDepartment
          />
        </div>

        {/* Priority filter + Sort */}
        <div style={{display:"flex",gap:4,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
          {reviewTaskCount > 0 && <button data-testid="focus-priority-review" onClick={() => setPri("REV")} style={{padding:"8px 14px",borderRadius:99,fontSize:12,fontWeight:700,minHeight:44,background:"var(--warn-tint)",color:"var(--warn)",outline:pri==="REV"?"2px solid var(--warn)":"none"}}>{t('quoteReview.lane')} ({reviewTaskCount})</button>}
          {["All","P1","P2","P3"].map(p => { const isActive = pri===p; return <button key={p} onClick={() => setPri(p)} style={{padding:"8px 14px",borderRadius:99,fontSize:12,fontWeight:600,minHeight:44,background:isActive?(p==="All"?"var(--navy-surface)":PRIORITIES[p]?.b):"transparent",color:isActive?(p==="All"?"var(--on-navy)":PRIORITIES[p]?.c):"var(--tm)"}}>{p}</button>; })}
          <div style={{flex:1}}/>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{padding:"5px 10px",borderRadius:8,border:"1px solid var(--bdr)",background:"var(--glass)",color:"var(--t2)",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            <option value="priority">{t('focus.sortPriority')}</option>
            <option value="due">{t('focus.sortDueDate')}</option>
            <option value="created">{t('focus.sortNewest')}</option>
          </select>
        </div>
      </div>}

      {/* ═══ TASKS LIST VIEW (with drag-and-drop reorder) ═══ */}
      {view === "tasks" && <div style={{padding:"0 12px 16px"}}>
        {orderedFiltered.length === 0 ? <div style={{textAlign:"center",padding:"60px 20px",color:"var(--tm)"}}><Anchor size={36} strokeWidth={1} style={{opacity:0.3,marginBottom:12}}/><div style={{fontSize:15,fontWeight:500}}>{t('focus.allClear')}</div></div>
        : <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
              {orderedFiltered.map((tk, i) => (
                <SortableTaskRow
                  key={tk.id}
                  task={tk}
                  index={i}
                  activeId={activeId}
                  onClick={() => { const raw = rawTasks.find(r => r.id === tk.id); if (raw) setTaskDetail(raw); else setModal(tk); }}
                  onCycleStatus={() => cycle(tk.id)}
                />
              ))}
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeDragTask ? (
                <div style={{
                  transform: 'rotate(2deg)',
                  opacity: 0.9,
                  pointerEvents: 'none',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  borderRadius: 14,
                }}>
                  <TaskCard task={activeDragTask} variant="focus" />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>}
        <button onClick={() => setShowDone(d => !d)} style={{width:"100%",padding:12,borderRadius:14,border:"1px dashed var(--bdr)",background:"transparent",color:"var(--tm)",fontSize:13,marginTop:4,minHeight:44,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}><ChevronDown size={14} style={{transform:showDone?"rotate(180deg)":"rotate(0)",transition:"transform 0.2s"}}/>{showDone ? t('focus.hideCompleted') : t('focus.showCompleted', { count: doneCount })}</button>
      </div>}

      {/* ═══ BOARD VIEW ═══ */}
      {view === "board" && (
        <div style={{padding:"0 16px 16px"}}>
          <FocusBoard
            tasks={filtered}
            onCycleStatus={cycle}
            onStatusChange={async (taskId, newStatus) => {
              const isPending = pendingTasks.some(t => t.id === taskId);
              if (isPending) {
                setPendingTasks(prev => prev.map(t => t.id !== taskId ? t : { ...t, st: normalizeTaskStatus(newStatus) }));
              } else if (taskId.startsWith('aaaaaaaa-') || taskId.startsWith('eeeeeeee-') || taskId.startsWith('ffffffff-')) {
                // Synthetic rows: no client-side mutator (rawTasks owned by context). Skip.
              } else {
                try { await handleUpdateStatus(taskId, newStatus); } catch { /* context rolls back */ }
              }
            }}
            onReorder={(newColumnOrderIds) => {
              // Within-column reorder on the kanban Board view.
              // newColumnOrderIds is the new order of task ids within ONE column.
              // Persist task_order for each changed row. Column-scoped so rows
              // in other columns keep their existing order.
              newColumnOrderIds.forEach((tid, i) => {
                if (tid.startsWith('aaaaaaaa-') || tid.startsWith('eeeeeeee-') || tid.startsWith('ffffffff-')) return; // synthetic
                handleUpdateTask(tid, { task_order: i + 1 }).catch(() => { /* context handles rollback */ });
              });
            }}
            onSelectTask={(t) => {
              const raw = rawTasks.find(r => r.id === t.id);
              if (raw) setTaskDetail(raw);
              else setModal(t);
            }}
          />
        </div>
      )}

      {/* FABs */}
      <div style={{position:"fixed",bottom:"calc(76px + env(safe-area-inset-bottom, 0px))",right:20,display:"flex",flexDirection:"column",gap:10,zIndex:90}}>
        <AddFab positioned={false} onClick={() => setModal("new")} ariaLabel={t('focus.newTaskAria')} testId="focus-add-task" />
      </div>

      {/* Modals */}
      {modal && <TaskEdit task={modal==="new"?null:modal} onSave={save} onClose={() => setModal(null)} onDel={del} projects={projects} people={people} peopleLookup={peopleLookup} bundles={bundles}/>}

      {/* ── Task Detail Sheet ──────────────────────── */}
      <ModalSheet open={!!taskDetail} onClose={() => setTaskDetail(null)} title={taskDetail?.task_name || t('common.task')}>
        {taskDetail && (
          <TaskDetailPanel
            task={taskDetail}
            people={people}
            projects={rawProjects}
            bundles={bundles}
            peopleLookup={peopleLookup}
            projectLookup={projectLookup}
            workTypes={schemaOptions?.workTypes}
            timeLogs={timeLogs}
            userRole={userRole}
            currentUserId={currentUserId}
            onStatusChange={async (taskId, newStatus) => {
              await handleUpdateStatus(taskId, newStatus);
              // FIX[shape]: merge raw snake_case onto the row, no fields wrapper
              setTaskDetail(prev => prev ? { ...prev, status: normalizeTaskStatus(newStatus) } : prev);
            }}
            onUpdate={async (updates) => {
              const result = await handleUpdateTask(taskDetail.id, updates);
              if (result?.success && result.record) {
                setTaskDetail(prev => prev ? ({ ...prev, ...result.record }) : result.record);
              }
              return result;
            }}
            onCreateTimeEntry={handleCreateTimeEntry}
            onDelete={async (id) => { await del(id); setTaskDetail(null); }}
            onClose={() => setTaskDetail(null)}
          />
        )}
      </ModalSheet>
    </div>
    </ViewContainer>
  );
}
